import type { TrackReferenceOrPlaceholder } from "@livekit/components-core"
import { isTrackReference, isWeb, log } from "@livekit/components-core"
import {
  ConnectionStateToast,
  LayoutContextProvider,
  type ReceivedChatMessage,
  useChat,
  useCreateLayoutContext,
  useDataChannel,
  useParticipants,
  usePinnedTracks,
  useRoomContext,
  useTracks,
} from "@livekit/components-react"
import { useMediaQuery, useTheme } from "@mui/material"
import * as Sentry from "@sentry/react"
import {
  LocalParticipant,
  RemoteParticipant,
  RoomEvent,
  Track,
} from "livekit-client"
import * as React from "react"
import CallLoading from "../../components/CustomVideoConference/CallLoading"
import { CallLayout } from "../../components/CustomVideoConference/Layouts"
import { CallShell } from "../../components/layout/CallShell"
import { AnalyticsEvents } from "../../types/Analytics"
import {
  CallDisconnectType,
  ChatMessageInputType,
  ChatMessageSender,
} from "../../types/Call"
import { postMessageToParent } from "../../utils/func"
import { LIVEKIT_ROOM_STORAGE_KEY } from "../../utils/general"
import { useAppContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"
import { useReportAvatarFreezes } from "../hooks/useReportAvatarFreezes"
import "./MyVideoConference.css"

interface VideoConferenceProps {
  setParticipants: (
    participants: (RemoteParticipant | LocalParticipant)[],
  ) => void
  duration: number
  maxDuration: number
  onUserMessage?: () => void
  onDisconnected?: (callDisconnectType: CallDisconnectType | null) => void
  onParticipantSpeaking?: (speakingParticipantIds: string[]) => void
  isEmbed: boolean
}

export function MyVideoConference({
  setParticipants,
  duration,
  maxDuration,
  onUserMessage,
  onDisconnected,
  onParticipantSpeaking,
  isEmbed,
}: VideoConferenceProps) {
  const [showChat, setShowChat] = React.useState(false)
  const [hostJoined, setHostJoined] = React.useState(false)
  const initTime = React.useRef(new Date())
  const hasLoggedHostJoin = React.useRef(false)

  const context = useAppContext()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const { chatMessages: sentAndReceivedMessages, send, isSending } = useChat()
  const [transcriptMessages, setTranscriptMessages] = React.useState<
    ReceivedChatMessage[]
  >([])
  const participants = useParticipants()

  const room = useRoomContext()

  const chatMessages = React.useMemo(
    () =>
      [...sentAndReceivedMessages, ...transcriptMessages].sort(
        (a, b) => a.timestamp - b.timestamp,
      ),
    [sentAndReceivedMessages, transcriptMessages],
  )

  const { logAnalyticsEvent } = useMixpanel()
  useReportAvatarFreezes()

  React.useEffect(() => {
    Sentry.addBreadcrumb({
      category: "video-conference",
      message: "Video conference initialized",
      level: "info",
      data: {
        initializationTime: initTime.current.toISOString(),
      },
    })
  }, [])

  React.useEffect(() => {
    const checkCall = async () => {
      const roomId = room.name
      if (!roomId) {
        room.disconnect()
        onDisconnected?.(CallDisconnectType.Other)
        return
      }

      const stored = sessionStorage.getItem(LIVEKIT_ROOM_STORAGE_KEY)
      if (stored) {
        try {
          const roomInfo = JSON.parse(stored)
          if (roomInfo.call_id === roomId) return
        } catch {
          room.disconnect()
          onDisconnected?.(CallDisconnectType.Other)
          return
        }
      }
      room.disconnect()
      onDisconnected?.(CallDisconnectType.InvalidConfiguration)
    }
    checkCall()
  }, [])

  React.useEffect(() => {
    setParticipants(participants)

    const agent = participants.find(
      (p) => p.isAgent && p.attributes?.["lk.agent.state"] !== undefined,
    )
    if (agent && !hasLoggedHostJoin.current) {
      const isAgentReady =
        agent.attributes &&
        (agent.attributes["lk.agent.state"] === "speaking" ||
          agent.attributes["lk.agent.state"] === "ready" ||
          agent.attributes["lk.agent.state"] === "thinking" ||
          agent.attributes["lk.agent.state"] === "listening")

      if (isAgentReady) {
        setHostJoined(true)
        hasLoggedHostJoin.current = true
        const hostJoinTime = new Date()

        Sentry.addBreadcrumb({
          category: "video-conference",
          message: "Host joined conference",
          level: "info",
          data: {
            hostJoinTime: hostJoinTime.toISOString(),
            timeTakenMs: hostJoinTime.getTime() - initTime.current.getTime(),
          },
        })

        // Notify parent iframe that agent has joined
        postMessageToParent({
          type: "AGENT_JOINED",
          callId: context.call?.id,
        })
      }
    }
  }, [participants])

  React.useEffect(() => {
    onParticipantSpeaking?.(
      participants.filter((p) => p.isSpeaking).map((p) => p.identity),
    )
  }, [participants, onParticipantSpeaking])

  const lastProcessedMessage = React.useRef<number>(0)

  React.useEffect(() => {
    const req = async () => {
      if (chatMessages.length === 0) {
        return
      }
      const lastMessage = chatMessages[chatMessages.length - 1]

      if (lastMessage.timestamp === lastProcessedMessage.current) {
        return
      }
      lastProcessedMessage.current = lastMessage.timestamp

      const sender = lastMessage.from.isAgent
        ? ChatMessageSender.ai
        : ChatMessageSender.user

      if (sender === ChatMessageSender.user) {
        onUserMessage?.()
      }

      const eventToSend =
        sender === ChatMessageSender.ai
          ? chatMessages.length === 1
            ? AnalyticsEvents.GreetingMessage
            : AnalyticsEvents.AnswerProvided
          : AnalyticsEvents.QuestionAsked
      logAnalyticsEvent(
        eventToSend,
        eventToSend === AnalyticsEvents.QuestionAsked
          ? {
              input_type: ChatMessageInputType.voice,
            }
          : undefined,
      )
    }
    req()
  }, [chatMessages])

  const onMessage = (msg: any) => {
    const message = new TextDecoder("utf-8").decode(msg.payload)
    // Shown in the chat only, because the agent already has it as speech
    setTranscriptMessages((previous) => [
      ...previous,
      {
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        message,
        from: room.localParticipant,
      },
    ])
  }

  useDataChannel("transcript", onMessage)

  const lastAutoFocusedScreenShareTrack =
    React.useRef<TrackReferenceOrPlaceholder | null>(null)

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  )

  const avatarVideo = useTracks([Track.Source.Camera], {
    onlySubscribed: true,
  }).find((track) => track.participant.isAgent)?.publication.videoTrack
    ?.mediaStreamTrack

  const [avatarVideoLive, setAvatarVideoLive] = React.useState(false)
  React.useEffect(() => {
    if (!avatarVideo) return
    if (!avatarVideo.muted) {
      setAvatarVideoLive(true)
      return
    }
    const onUnmute = () => setAvatarVideoLive(true)
    avatarVideo.addEventListener("unmute", onUnmute)
    return () => avatarVideo.removeEventListener("unmute", onUnmute)
  }, [avatarVideo])

  const layoutContext = useCreateLayoutContext()

  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare)

  const focusTrack = usePinnedTracks(layoutContext)?.[0]

  React.useEffect(() => {
    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      log.debug("Auto set screen share focus:", {
        newScreenShareTrack: screenShareTracks[0],
      })
      layoutContext.pin.dispatch?.({
        msg: "set_pin",
        trackReference: screenShareTracks[0],
      })
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0]
    } else if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some(
        (track) =>
          track.publication.trackSid ===
          lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
      )
    ) {
      log.debug("Auto clearing screen share focus.")
      layoutContext.pin.dispatch?.({ msg: "clear_pin" })
      lastAutoFocusedScreenShareTrack.current = null
    }
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (tr) =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source,
      )
      if (
        updatedFocusTrack !== focusTrack &&
        isTrackReference(updatedFocusTrack)
      ) {
        layoutContext.pin.dispatch?.({
          msg: "set_pin",
          trackReference: updatedFocusTrack,
        })
      }
    }
  }, [
    screenShareTracks
      .map(
        (ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`,
      )
      .join(),
    focusTrack?.publication?.trackSid,
  ])

  React.useEffect(() => {
    if (focusTrack == null && !isMobile) {
      const agentTrack = tracks.find(
        (track) =>
          track.source === Track.Source.Camera &&
          track.participant.isSpeaking &&
          track.participant.isAgent,
      )
      if (agentTrack) {
        layoutContext.pin.dispatch?.({
          msg: "set_pin",
          trackReference: agentTrack,
        })
      }
    }
  }, [tracks])

  const closeChat = React.useCallback(() => {
    setShowChat(false)
  }, [])

  const toggleChat = React.useCallback(() => {
    setShowChat((prev) => !prev)
  }, [])

  const handleDisconnect = () => {
    room.disconnect()
    onDisconnected?.(null)
  }

  const localCameraTrack = tracks.find(
    (track) =>
      track.participant.isLocal && track.source === Track.Source.Camera,
  )?.publication

  if (!hostJoined && !avatarVideoLive)
    return <CallLoading agent={context.agent} isEmbed={isEmbed} />

  return (
    <CallShell isEmbed={isEmbed}>
      {isWeb() && (
        <LayoutContextProvider value={layoutContext}>
          <CallLayout
            tracks={tracks.filter(
              (track) =>
                !(track.participant.isAgent && !isTrackReference(track)),
            )}
            showChat={showChat}
            isEmbed={isEmbed}
            audioState={context.audioEnabled ?? true}
            videoState={context.videoEnabled ?? true}
            localCameraTrack={localCameraTrack}
            handleDisconnect={handleDisconnect}
            toggleChat={toggleChat}
            duration={duration}
            maxDuration={maxDuration}
            closeChat={closeChat}
            chatMessages={chatMessages}
            isSending={isSending}
            send={send}
            agent={context.agent}
          />
        </LayoutContextProvider>
      )}
      <ConnectionStateToast />
    </CallShell>
  )
}
